import React, { useEffect, useMemo, useState } from 'react';
import CampaignEmailRichTextEditor from './CampaignEmailRichTextEditor';
import CampaignEmailLivePreview, {
  CampaignEmailVariablesLegend,
} from './CampaignEmailLivePreview';
import EmailWizardFrame from './EmailWizardFrame';
import HubupNotificationPickerFields, {
  emptyHubupSendState,
  hubupSendStateFromSource,
  hubupStateToEmailOverride,
  resolveHubupPreviewNotification,
} from './HubupNotificationPickerFields';
import CampaignEmailTemplatePicker from './CampaignEmailTemplatePicker';
import { canUseHubupEmailFeatures } from '../../utils/campaignHubupAccess';
import {
  campaignEmailBannerImageUrl,
  campaignEmailTemplateBannerImageUrl,
  sendCampaignEmailing,
  uploadCampaignEmailBanner,
} from '../../api/campaigns';
import { sendProspectMail, fetchProspects } from '../../api/prospects';
import { productImageUrl } from '../../api/products';
import {
  campaignEmailTemplateId,
  campaignEmailTemplatesFromCampaign,
  campaignWithEmailTemplateSource,
  isCampaignEmailSourceSendable,
  latestCampaignEmailTemplate,
  templateUsesHubupVariable,
} from '../../utils/campaignEmailTemplate';
import ProductCardEmailSettingsFields from './ProductCardEmailSettingsFields';
import {
  hubupButtonDesignFromSource,
  hubupButtonDesignToEmailFields,
  productCardOptionsFromSource,
  productCardOptionsToEmailFields,
} from '../../utils/emailButtonStyles';
import { useUserAccess } from '../../context/UserAccessContext';
import { useCrmMailQuota } from '../../hooks/useCrmMailQuota';
import { useCampaignHubupPublishedNotifications } from '../../hooks/useCampaignHubupPublishedNotifications';
import {
  campaignHasProducts,
  campaignHubupConfigured,
} from '../../utils/campaignCapabilities';

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

function campaignTagNames(campaign) {
  return Array.isArray(campaign?.tags)
    ? campaign.tags
        .map((t) => (typeof t === 'object' && t?.name ? t.name : String(t || '')))
        .map((t) => t.trim())
        .filter(Boolean)
    : [];
}

function normalizeCampaignForApi(campaign) {
  if (!campaign) return null;
  return {
    ...campaign,
    _id: campaign._id || campaign.id,
    hubupNotificationHistory: campaign.hubupNotificationHistory || [],
  };
}

/**
 * mode:
 * - campaign: emailing campagne (template → destinataires → hubup? → aperçu/envoi)
 * - prospect: annuaire sans appel (template → hubup? → aperçu/envoi)
 * - prospect_product: envoi produit pendant appel (template → produit → aperçu/envoi, sans HubUP)
 */
export default function CampaignEmailSendWizard({
  mode = 'campaign',
  campaign: campaignProp,
  campaigns = null,
  contact = null,
  initialTemplateId = '',
  initialProductId = '',
  onClose,
  onSuccess,
  layout = 'modal',
}) {
  const { user } = useUserAccess();
  const { canSendMail, refetchCompanies } = useCrmMailQuota();

  const [campaignId, setCampaignId] = useState(
    () => campaignProp?.id || campaignProp?._id || campaigns?.[0]?.id || '',
  );
  const campaign = useMemo(() => {
    if (campaignProp) return normalizeCampaignForApi(campaignProp);
    const list = campaigns || [];
    return normalizeCampaignForApi(
      list.find((c) => (c.id || c._id) === campaignId) || list[0],
    );
  }, [campaignProp, campaigns, campaignId]);

  const hubupAllowed =
    canUseHubupEmailFeatures(user, campaign) && campaignHubupConfigured(campaign);
  const hasProducts = campaignHasProducts(campaign);
  const { published: hubupPublished } = useCampaignHubupPublishedNotifications(campaign);
  const isProductCall = mode === 'prospect_product';

  const savedTemplates = campaignEmailTemplatesFromCampaign(campaign);
  const [templateId, setTemplateId] = useState(
    () =>
      initialTemplateId ||
      campaignEmailTemplateId(latestCampaignEmailTemplate(campaign)),
  );
  const selectedTemplate = useMemo(
    () => savedTemplates.find((t) => campaignEmailTemplateId(t) === templateId) || null,
    [savedTemplates, templateId],
  );

  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [bannerUrl, setBannerUrl] = useState('');
  const [bannerKey, setBannerKey] = useState('');
  const [bannerOriginalName, setBannerOriginalName] = useState('');
  const [uploadingBanner, setUploadingBanner] = useState(false);

  const [productId, setProductId] = useState(initialProductId || '');
  const [productCardOptions, setProductCardOptions] = useState(() =>
    productCardOptionsFromSource(null),
  );
  const [hubupState, setHubupState] = useState(emptyHubupSendState);

  const [sendToProspects, setSendToProspects] = useState(false);
  const [sendToSingleEmail, setSendToSingleEmail] = useState(false);
  const [singleEmail, setSingleEmail] = useState('');
  const [prospectEmailCount, setProspectEmailCount] = useState(0);
  const [prospectTotal, setProspectTotal] = useState(0);
  const [loadingProspects, setLoadingProspects] = useState(false);

  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    const source = selectedTemplate || campaign || {};
    setSubject(source.emailTemplateSubject || '');
    setHtml(source.emailTemplateHtml || '');
    setBannerUrl(source.emailTemplateBannerUrl || '');
    setBannerKey(source.emailTemplateBannerKey || '');
    setBannerOriginalName(source.emailTemplateBannerOriginalName || '');
    setHubupState(emptyHubupSendState());
    const cardSource = selectedTemplate || campaign || {};
    setProductCardOptions(productCardOptionsFromSource(cardSource));
    if (!isProductCall && Array.isArray(campaign?.products) && campaign.products.length) {
      setProductId((prev) => {
        if (prev) return prev;
        const first = campaign.products[0];
        const pid = first?.id || first?._id;
        return pid ? String(pid) : prev;
      });
    }
  }, [selectedTemplate, campaign?._id, templateId, isProductCall, campaign?.products]);

  const designSource = useMemo(() => {
    const merged = hubupButtonDesignFromSource(selectedTemplate, campaign);
    return {
      ...(selectedTemplate || campaign || {}),
      ...hubupButtonDesignToEmailFields(merged),
    };
  }, [selectedTemplate, campaign]);
  const needsHubupStep = hubupAllowed && templateUsesHubupVariable(html);

  const steps = useMemo(() => {
    const confirmHint = needsHubupStep
      ? 'Choisissez la notification HubUP, vérifiez l’aperçu puis envoyez.'
      : 'Vérifiez le rendu avant envoi.';
    if (isProductCall) {
      return [
        { id: 'template', label: 'Template', hint: 'Choisissez le modèle de départ.' },
        { id: 'product', label: 'Produit', hint: 'Fiche produit incluse dans le mail.' },
        { id: 'confirm', label: 'Aperçu & envoi', hint: confirmHint },
      ];
    }
    if (mode === 'prospect') {
      return [
        { id: 'template', label: 'Template', hint: 'Bibliothèque de la campagne.' },
        { id: 'confirm', label: 'Aperçu & envoi', hint: confirmHint },
      ];
    }
    return [
      { id: 'template', label: 'Template', hint: 'Quel modèle envoyer ?' },
      { id: 'audience', label: 'Destinataires', hint: 'Campagne ou email de test.' },
      { id: 'confirm', label: 'Aperçu & envoi', hint: confirmHint },
    ];
  }, [mode, isProductCall, needsHubupStep]);

  const [stepIndex, setStepIndex] = useState(0);
  const stepId = steps[stepIndex]?.id;

  useEffect(() => {
    setStepIndex((i) => Math.min(i, Math.max(0, steps.length - 1)));
  }, [steps.length]);

  useEffect(() => {
    if (mode !== 'campaign' || stepId !== 'audience') return;
    const tags = campaignTagNames(campaign);
    let cancelled = false;
    (async () => {
      setLoadingProspects(true);
      try {
        if (tags.length === 0) {
          if (!cancelled) {
            setProspectTotal(0);
            setProspectEmailCount(0);
          }
          return;
        }
        const res = await fetchProspects({ tags, limit: 200 });
        if (cancelled) return;
        const prospects = res?.data?.prospects || [];
        setProspectTotal(res?.data?.pagination?.total || prospects.length);
        setProspectEmailCount(
          prospects.filter((p) => String(p?.email || '').trim()).length,
        );
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoadingProspects(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, stepId, campaign?._id]);

  const emailSourceForPreview = useMemo(() => {
    const base = campaignWithEmailTemplateSource(campaign, {
      emailTemplateSubject: subject,
      emailTemplateHtml: html,
      emailTemplateBannerUrl: bannerUrl,
      emailTemplateBannerKey: bannerKey,
      emailTemplateBannerOriginalName: bannerOriginalName,
      ...hubupStateToEmailOverride(hubupState),
    });
    return base;
  }, [campaign, subject, html, bannerUrl, bannerKey, bannerOriginalName, hubupState]);

  const bannerPreviewUrl =
    selectedTemplate && bannerKey && bannerKey === selectedTemplate.emailTemplateBannerKey
      ? campaignEmailTemplateBannerImageUrl(campaign, {
          ...selectedTemplate,
          emailTemplateBannerUrl: bannerUrl,
          emailTemplateBannerKey: bannerKey,
          emailTemplateBannerOriginalName: bannerOriginalName,
        })
      : bannerUrl || campaignEmailBannerImageUrl(emailSourceForPreview);

  const hubupPreview = needsHubupStep
    ? resolveHubupPreviewNotification(campaign, hubupState, designSource, hubupPublished)
    : null;

  const productPreview = useMemo(() => {
    if (!productId || !campaign?.products) return null;
    const p = campaign.products.find(
      (x) => String(x.id || x._id) === String(productId),
    );
    if (!p) return null;
    const pid = p.id || p._id;
    return {
      name: p.name,
      description: p.description,
      price: p.price,
      imageUrl: productImageUrl({
        ...p,
        _id: pid,
        companyId: p.companyId || campaign.companyId,
      }),
    };
  }, [productId, campaign]);

  const hasTemplate = isCampaignEmailSourceSendable({
    emailTemplateHtml: html,
  });

  const canProceedTemplate = hasTemplate && Boolean(templateId || html.trim());
  const canProceedAudience =
    (sendToProspects && prospectEmailCount > 0) ||
    (sendToSingleEmail && isValidEmail(singleEmail));
  const canProceedProduct = Boolean(productId);
  const canSendFinal = useMemo(() => {
    if (!canSendMail || sending || !hasTemplate) return false;
    if (needsHubupStep && !String(hubupState.messageId || '').trim()) return false;
    if (mode === 'campaign') {
      return canProceedAudience;
    }
    if (mode === 'prospect' || mode === 'prospect_product') {
      return Boolean(contact?.id || contact?._id);
    }
    return false;
  }, [
    canSendMail,
    sending,
    hasTemplate,
    needsHubupStep,
    hubupState.messageId,
    mode,
    canProceedAudience,
    contact,
  ]);

  const nextDisabled = useMemo(() => {
    if (stepId === 'template') return !canProceedTemplate;
    if (stepId === 'audience') return !canProceedAudience;
    if (stepId === 'product') return !canProceedProduct;
    if (stepId === 'confirm') return !canSendFinal;
    return false;
  }, [
    stepId,
    canProceedTemplate,
    canProceedAudience,
    canProceedProduct,
    canSendFinal,
  ]);

  const handleUploadBanner = async (file) => {
    const cid = campaign?._id || campaign?.id;
    if (!cid || !file) return;
    try {
      setUploadingBanner(true);
      const res = await uploadCampaignEmailBanner(cid, file);
      const c = res?.data?.campaign;
      setBannerUrl(c?.emailTemplateBannerUrl || '');
      setBannerKey(c?.emailTemplateBannerKey || '');
      setBannerOriginalName(c?.emailTemplateBannerOriginalName || '');
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Upload bandeau impossible.');
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleSend = async () => {
    if (!canSendFinal) return;
    setSending(true);
    setError(null);
    setSuccess(null);
    try {
      const cid = campaign?._id || campaign?.id;
      const emailOverride = {
        emailTemplateSubject: subject,
        emailTemplateHtml: html,
        emailTemplateBannerUrl: bannerUrl || null,
        emailTemplateBannerKey: bannerKey || null,
        emailTemplateBannerOriginalName: bannerOriginalName || null,
        emailTemplateBannerTemplateId:
          selectedTemplate && bannerKey === selectedTemplate.emailTemplateBannerKey
            ? campaignEmailTemplateId(selectedTemplate)
            : null,
        ...(needsHubupStep ? hubupStateToEmailOverride(hubupState) : {}),
        ...hubupButtonDesignToEmailFields(hubupButtonDesignFromSource(designSource)),
        ...productCardOptionsToEmailFields(productCardOptions),
      };

      if (mode === 'campaign') {
        const res = await sendCampaignEmailing(cid, {
          sendToProspects,
          singleEmail: sendToSingleEmail ? singleEmail.trim() : '',
          templateId: templateId || null,
          productId: productCardOptions.includeProductCard ? productId || null : null,
          emailOverride,
        });
        setSuccess(`Email envoyé à ${res?.data?.recipientCount || 0} destinataire(s).`);
      } else {
        const prospectId = contact?.id || contact?._id;
        await sendProspectMail(prospectId, {
          campaignId: cid,
          templateId: templateId || null,
          mailContext: isProductCall ? 'prospect_product' : 'prospect_directory',
          productId: isProductCall ? productId : null,
          emailOverride,
        });
        setSuccess('Email envoyé au prospect.');
      }
      await refetchCompanies?.();
      onSuccess?.();
      setTimeout(onClose, 1200);
    } catch (e) {
      console.error(e);
      setError(e?.message || "Impossible d'envoyer l'email.");
    } finally {
      setSending(false);
    }
  };

  const goNext = () => {
    if (stepId === 'confirm') {
      handleSend();
      return;
    }
    setStepIndex((i) => Math.min(i + 1, steps.length - 1));
  };

  const title =
    mode === 'campaign'
      ? `Emailing — ${campaign?.name || 'Campagne'}`
      : isProductCall
        ? `Mail produit — ${contact?.name || contact?.fullName || 'Prospect'}`
        : `Email prospect — ${contact?.name || contact?.fullName || 'Prospect'}`;

  return (
    <EmailWizardFrame
      layout={layout}
      backLabel={layout === 'page' ? 'Retour' : 'Annuler'}
      title={title}
      subtitle={
        !canSendMail
          ? 'Quota e-mail épuisé — rechargez un pack pour envoyer.'
          : undefined
      }
      steps={steps}
      stepIndex={stepIndex}
      onClose={onClose}
      onBack={() => setStepIndex((i) => Math.max(0, i - 1))}
      onNext={goNext}
      nextLabel={stepId === 'confirm' ? (sending ? 'Envoi…' : 'Envoyer') : 'Continuer'}
      nextDisabled={nextDisabled || sending || (stepId === 'confirm' && !canSendMail)}
      footerExtra={
        <div className="space-y-1">
          {error ? <p className="text-xs text-error">{error}</p> : null}
          {success ? <p className="text-xs text-success">{success}</p> : null}
          {!canSendMail ? (
            <p className="text-xs text-muted-foreground">Envoi grisé : quota épuisé.</p>
          ) : null}
        </div>
      }
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start">
        <div className="space-y-4 min-w-0">
          {campaigns && campaigns.length > 1 && stepId === 'template' ? (
            <div>
              <label className="mb-1 block text-xs font-medium text-foreground">
                Campagne
              </label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={campaignId}
                onChange={(e) => setCampaignId(e.target.value)}
              >
                {campaigns.map((c) => (
                  <option key={c.id || c._id} value={c.id || c._id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {stepId === 'template' ? (
            <>
              <div>
                <p className="mb-2 text-xs font-medium text-foreground">
                  Choisissez un template
                </p>
                <CampaignEmailTemplatePicker
                  campaign={campaign}
                  templates={savedTemplates}
                  selectedId={templateId}
                  onSelect={setTemplateId}
                />
              </div>
              <CampaignEmailVariablesLegend
                showHubup={hubupAllowed && !isProductCall}
              />
              <div>
                <label className="mb-1 block text-xs font-medium text-foreground">
                  Sujet
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-foreground">
                  Contenu
                </label>
                <CampaignEmailRichTextEditor
                  value={html}
                  onChange={setHtml}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-foreground">
                  Bandeau
                </label>
                <input
                  type="file"
                  accept="image/*"
                  disabled={uploadingBanner}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleUploadBanner(f);
                    e.target.value = '';
                  }}
                  className="w-full text-sm"
                />
              </div>
            </>
          ) : null}

          {stepId === 'audience' ? (
            <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-4">
              <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border bg-background p-3">
                <input
                  type="checkbox"
                  checked={sendToProspects}
                  onChange={(e) => setSendToProspects(e.target.checked)}
                  className="mt-0.5"
                />
                <span className="text-sm">
                  <span className="block font-medium">Tous les prospects de la campagne</span>
                  <span className="text-xs text-muted-foreground">
                    {loadingProspects
                      ? 'Chargement…'
                      : `${prospectEmailCount} email(s) / ${prospectTotal} prospect(s)`}
                  </span>
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border bg-background p-3">
                <input
                  type="checkbox"
                  checked={sendToSingleEmail}
                  onChange={(e) => setSendToSingleEmail(e.target.checked)}
                  className="mt-0.5"
                />
                <span className="flex-1 text-sm">
                  <span className="block font-medium">Email de test</span>
                  <input
                    type="email"
                    value={singleEmail}
                    disabled={!sendToSingleEmail}
                    onChange={(e) => setSingleEmail(e.target.value)}
                    className="mt-2 w-full rounded-md border border-input px-3 py-2 text-sm disabled:opacity-50"
                    placeholder="vous@exemple.fr"
                  />
                </span>
              </label>
            </div>
          ) : null}

          {stepId === 'product' ? (
            <div>
              <label className="mb-1 block text-xs font-medium text-foreground">
                Produit
              </label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
              >
                {(campaign?.products || []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {typeof p.price === 'number' ? ` — ${p.price} €` : ''}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {stepId === 'confirm' ? (
            <div className="space-y-4">
              {!isProductCall && hasProducts ? (
                <div className="space-y-3">
                  <ProductCardEmailSettingsFields
                    value={productCardOptions}
                    onChange={setProductCardOptions}
                  />
                  {productCardOptions.includeProductCard &&
                  (campaign?.products?.length ?? 0) > 1 ? (
                    <div>
                      <label className="mb-1 block text-xs font-medium text-foreground">
                        Produit à inclure
                      </label>
                      <select
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        value={productId}
                        onChange={(e) => setProductId(e.target.value)}
                      >
                        {(campaign?.products || []).map((p) => {
                          const pid = p.id || p._id;
                          return (
                            <option key={pid} value={pid}>
                              {p.name}
                              {typeof p.price === 'number' ? ` — ${p.price} €` : ''}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  ) : null}
                </div>
              ) : null}
              {needsHubupStep ? (
                <HubupNotificationPickerFields
                  campaign={campaign}
                  value={hubupState}
                  onChange={setHubupState}
                  required
                />
              ) : null}
              <div className="rounded-lg border border-border bg-muted/20 p-3 text-sm text-muted-foreground">
                {mode === 'campaign' ? (
                  <p>
                    Envoi vers{' '}
                    {sendToProspects ? `${prospectEmailCount} prospect(s)` : ''}
                    {sendToProspects && sendToSingleEmail ? ' et ' : ''}
                    {sendToSingleEmail ? `test ${singleEmail}` : ''}
                  </p>
                ) : (
                  <p>
                    Destinataire :{' '}
                    <strong className="text-foreground">
                      {contact?.email || contact?.name}
                    </strong>
                  </p>
                )}
              </div>
            </div>
          ) : null}
        </div>

        <CampaignEmailLivePreview
          sticky={false}
          emailPreviewMode="send"
          subject={subject}
          html={html}
          bannerUrl={bannerPreviewUrl}
          productPreview={productPreview}
          productCardOptions={productCardOptions}
          hubupNotification={hubupPreview}
        />
      </div>
    </EmailWizardFrame>
  );
}
